import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, Move, Check, X } from 'lucide-react';

interface ImageCropModalProps {
  open: boolean;
  imageFile: File | null;
  onClose: () => void;
  onCrop: (croppedBlob: Blob) => void;
  size?: number; // Output size in pixels (square)
}

export function ImageCropModal({
  open,
  imageFile,
  onClose,
  onCrop,
  size = 256,
}: ImageCropModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const imageRef = useRef<HTMLImageElement | null>(null);

  // Load image when file changes
  useEffect(() => {
    if (!imageFile) {
      setImageSrc(null);
      setImageLoaded(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target?.result as string);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // Load image dimensions
  useEffect(() => {
    if (!imageSrc) return;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });
      setImageLoaded(true);
      // Reset position and zoom
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
    },
    [position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleCrop = useCallback(() => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    const img = imageRef.current;
    const containerSize = 280; // Match the visible area size

    const baseScale = Math.max(containerSize / img.width, containerSize / img.height);
    const baseDisplayWidth = img.width * baseScale;
    const baseDisplayHeight = img.height * baseScale;

    const displayWidth = baseDisplayWidth * zoom;
    const displayHeight = baseDisplayHeight * zoom;

    // Offset in container coordinates (0..containerSize)
    // With our render approach, image is centered at container center, then translated by `position`, then scaled.
    const offsetX = (containerSize - displayWidth) / 2 + position.x;
    const offsetY = (containerSize - displayHeight) / 2 + position.y;

    // Source coordinates (what part of the original image to use)
    const srcX = -offsetX / (baseScale * zoom);
    const srcY = -offsetY / (baseScale * zoom);
    const srcWidth = containerSize / (baseScale * zoom);
    const srcHeight = containerSize / (baseScale * zoom);

    // Draw the cropped portion
    ctx.drawImage(
      img,
      srcX,
      srcY,
      srcWidth,
      srcHeight,
      0,
      0,
      size,
      size
    );

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCrop(blob);
        }
      },
      'image/jpeg',
      0.9
    );
  }, [size, zoom, position, onCrop]);

  const containerSize = 280;
  const baseScale = imageSize.width > 0
    ? Math.max(containerSize / imageSize.width, containerSize / imageSize.height)
    : 1;

  const baseDisplayWidth = imageSize.width * baseScale;
  const baseDisplayHeight = imageSize.height * baseScale;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="w-4 h-4" />
            Ajustar Foto
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Crop area */}
          <div
            ref={containerRef}
            className="relative mx-auto overflow-hidden rounded-full bg-muted cursor-move select-none"
            style={{ width: containerSize, height: containerSize }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {imageLoaded && imageSrc && (
              <img
                src={imageSrc}
                alt="Crop preview"
                className="absolute pointer-events-none"
                style={{
                  width: baseDisplayWidth,
                  height: baseDisplayHeight,
                  left: '50%',
                  top: '50%',
                  transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  transformOrigin: 'center',
                }}
                draggable={false}
              />
            )}
            {/* Circle overlay guide */}
            <div
              className="absolute inset-0 rounded-full ring-4 ring-primary/30 pointer-events-none"
            />
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Arraste para posicionar a foto
          </p>

          {/* Zoom control */}
          <div className="flex items-center gap-3 px-4">
            <ZoomOut className="w-4 h-4 text-muted-foreground" />
            <Slider
              value={[zoom]}
              onValueChange={([v]) => setZoom(v)}
              min={1}
              max={3}
              step={0.1}
              className="flex-1"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>

        {/* Hidden canvas for cropping */}
        <canvas ref={canvasRef} className="hidden" />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
          <Button onClick={handleCrop}>
            <Check className="w-4 h-4 mr-1" />
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
